import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Conversation from 'components/common/Conversation';
import QuickTemplates from 'components/common/QuickTemplates';
import { useFeedback } from 'contexts/FeedbackContext';
import { onMessage } from 'firebase/messaging';
import useCurrentUser from 'hooks/useCurrentUser';
import useIsMobile from 'hooks/useIsMobile';
import ErrorPage from 'pages/Error/ErrorPage';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetBusinessUnitQuery } from 'services/api/businessUnits.api';
import {
  useGetClientLeadsQuery,
  useGetClientQuery,
  useUpdateAdminEditableAttributesMutation,
  useUpdateEditableAttributesMutation,
} from 'services/api/clients.api';
import { useGetClientMessagesQuery, useGetNewClientMessagesMutation } from 'services/api/messages';
import type { Client, EditableAdminClientAttributes } from 'services/types/client';
import type { Message, MessageStatus } from 'services/types/message';
import type { ScheduledMessage } from 'services/types/scheduledMessage';
import {
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SHADOW_CARD,
  SURFACE,
  SURFACE_CHAT,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';
import { subscribeChannel } from 'utils/pusherClient';
import formatRut from 'utils/validations/formatRut';
import { getWhatsAppWindowExpiry } from 'utils/whatsAppWindow';

import { messaging } from 'firebaseConfig';
import type { NewMessageEvent } from '../Clients';
import ChatFooter from './ChatFooter';
import ChatHeader from './ChatHeader';
import ClientInfoPanel from './ClientInfoPanel';
import ClientSidebar from './components/ClientSidebar';
import EditClientForm from './EditClientForm';
import TemplateMessageModal from './TemplateMessageModal';

const Chat = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const { isMobile } = useIsMobile();

  const locationState = location.state as { clientsList?: Client[]; newEvents?: NewMessageEvent[]; from?: string };
  // Extract clientsList from navigation state and manage as state
  const [clientsList, setClientsList] = useState<Client[]>(locationState?.clientsList || []);
  const [newEvents, setNewEvents] = useState<NewMessageEvent[]>(locationState?.newEvents || []);

  const {
    data: client,
    refetch: refetchClient,
    isLoading,
    isFetching,
  } = useGetClientQuery(clientId ?? '', {
    refetchOnMountOrArgChange: true,
    skip: !clientId,
  });
  const {
    data: messages,
    refetch: refetchMessages,
    isFetching: isLoadingMessages,
  } = useGetClientMessagesQuery(clientId ?? '', {
    refetchOnMountOrArgChange: true,
    skip: !clientId,
  });
  const [getNewClientMessages, { data: messagesData }] = useGetNewClientMessagesMutation();
  const { data: clientDealership } = useGetBusinessUnitQuery(client?.businessUnitId ?? 0, {
    skip: !client?.businessUnitId,
  });
  const [updateClient, { data: updatedClient, isLoading: isSavingClient, error: updateClientError }] =
    useUpdateAdminEditableAttributesMutation();
  const [
    updateClientEditable,
    { data: updatedClientEditable, isLoading: isSavingClientEditable, error: updateClientEditableError },
  ] = useUpdateEditableAttributesMutation();
  const { data: clientLeads, refetch: refetchClientLeads } = useGetClientLeadsQuery(clientId ?? '', {
    refetchOnMountOrArgChange: true,
    skip: !clientId,
  });
  const [clientDraft, setClientDraft] = useState<EditableAdminClientAttributes | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [conversation, setConversation] = useState<Message[]>([]);
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [isFilteredConversation, setIsFilteredConversation] = useState<boolean>(
    currentUser?.filterConversationsByDefault || false
  );
  const [showAttributes, setShowAttributes] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { notifySaved } = useFeedback();
  const [openTemplateModal, setOpenTemplateModal] = useState(false);

  // Email has no 24h send window: the conversation is always open and there is no expiry to show.
  const windowExpiresAt = useMemo<Date | null>(
    () => (client?.chatType === 'email' ? null : getWhatsAppWindowExpiry(client?.clientLastMessageDate)),
    [client?.clientLastMessageDate, client?.chatType]
  );

  const isConversationActive =
    client?.chatType === 'email' || (windowExpiresAt !== null && windowExpiresAt.getTime() > Date.now());

  useEffect(() => {
    if (messages) {
      setConversation(messages);
    }
  }, [messages]);

  // Update clientsList with current client information
  // biome-ignore lint/correctness/useExhaustiveDependencies: clientsList.length would loop — only react to client identity changes
  useEffect(() => {
    if (client && clientsList.length > 0) {
      setClientsList((prevList) => {
        const index = prevList.findIndex((c) => c.id === client.id);
        if (index !== -1) {
          const newList = [...prevList];
          newList[index] = client;
          return newList;
        }
        return prevList;
      });
    }
  }, [client]);

  useEffect(() => {
    if (client) {
      setClientDraft({
        firstName: client.firstName,
        lastName: client.lastName,
        hasValidName: client.hasValidName,
        rut: client.rut || undefined,
        dni: client.dni || undefined,
        email: client.email,
        salary: client.salary,
        savingsAmount: client.savingsAmount || undefined,
        hasVehicleAsPartPayment: client.hasVehicleAsPartPayment,
        requiresFinancing: client.requiresFinancing,
        monthlyPaymentsAmount: client.monthlyPaymentsAmount || undefined,
        budgetAmount: client.budgetAmount || undefined,
        modelsOfInterest: client.modelsOfInterest || [],
        disabledAutomaticResponses: client.disabledAutomaticResponses || false,
        disabledFollowUps: client.disabledFollowUps || false,
        isTestClient: client.isTestClient || false,
        brandOfInterest: client.brandOfInterest || undefined,
        aiSafetyStatus: client.aiSafetyStatus || undefined,
        notes: client.notes || undefined,
        gender: client.gender || undefined,
        dateOfBirth: client.dateOfBirth || undefined,
        nationality: client.nationality || undefined,
        address: client.address && typeof client.address === 'object' ? client.address : undefined,
      });
    }
    if (client?.scheduledMessages) {
      setScheduledMessages(client.scheduledMessages);
    }
  }, [client]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetchClient ref changes per render and would loop
  useEffect(() => {
    if (updatedClient || updatedClientEditable) {
      refetchClient().catch((err) => {
        console.error(err);
      });
    }
  }, [updatedClient, updatedClientEditable]);

  const handleUpdateMessageStatus = (
    updatedMessages: { id: number; status: MessageStatus }[],
    refetchStatusMessages: boolean
  ) => {
    setConversation((prevConversation) =>
      prevConversation.map((m) => {
        const newStatus = updatedMessages.find((um) => um.id === m.id)?.status;
        if (newStatus) {
          return { ...m, status: newStatus };
        }
        return m;
      })
    );
    const notFoundMessages = updatedMessages
      .filter((um) => !conversation.find((m) => m.id === um.id))
      .map((um) => um.id);
    if (notFoundMessages.length > 0 && refetchStatusMessages && clientId) {
      getNewClientMessages({ clientId, messageId: Math.min(...notFoundMessages) }).catch((err) => {
        console.error(err);
      });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleUpdateMessageStatus is redefined every render — adding it loops
  useEffect(() => {
    if (messagesData) {
      setConversation((prevConversation) => {
        // If a message already exists in the conversation, replace it otherwise, add the new message to the conversation
        const updatedConversation = [...prevConversation];
        return messagesData.newMessages
          .reduce((acc, newMessage) => {
            const existingIndex = acc.findIndex((m) => m.id === newMessage.id);
            if (existingIndex !== -1) {
              acc[existingIndex] = newMessage;
            } else {
              acc.push(newMessage);
            }
            return acc;
          }, updatedConversation)
          .sort((a, b) => a.id - b.id);
      });
      handleUpdateMessageStatus(messagesData.oldMessagesStatuses, false);
      // The 24h window lives on the client, not on the loaded conversation, so an inbound message that
      // arrives while the chat is open only reopens it once the client is refetched.
      if (messagesData.newMessages.some((message) => message.role === 'user')) {
        refetchClient().catch((err) => {
          console.error(err);
        });
      }
    }
  }, [messagesData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: getNewClientMessages and handleUpdateMessageStatus refs change per render and would loop
  useEffect(() => {
    if (client?.id) {
      const channelName = `chat-${client.id}`;
      const { channel, unsubscribe } = subscribeChannel(channelName);
      const newMessageHandler = (data: { messageId: number }) => {
        getNewClientMessages({ clientId: String(client.id), messageId: data.messageId }).catch((err) => {
          console.error(err);
        });
      };
      const updateMessagesHandler = (data: { messages: { id: number; status: MessageStatus }[] }) => {
        handleUpdateMessageStatus(data.messages, true);
      };
      channel.bind('new-message', newMessageHandler);
      channel.bind('update-messages', updateMessagesHandler);
      return () => {
        channel.unbind('new-message', newMessageHandler);
        channel.unbind('update-messages', updateMessagesHandler);
        unsubscribe();
      };
    }
    return () => {};
  }, [client?.id]);

  // Subscribe to business unit messages for new events notification in sidebar
  const clientsListRef = useRef(clientsList);
  useEffect(() => {
    clientsListRef.current = clientsList;
  }, [clientsList]);

  useEffect(() => {
    if (!isMobile && client?.businessUnitId) {
      const channelName = `business-unit-message-${client.businessUnitId}`;
      const { channel, unsubscribe } = subscribeChannel(channelName);
      const handler = (event: NewMessageEvent) => {
        if (clientsListRef.current.some((c) => c.id === event.clientId)) {
          setNewEvents((prev) => [...prev, event]);
        }
      };
      channel.bind('new-message', handler);
      return () => {
        channel.unbind('new-message', handler);
        unsubscribe();
      };
    }
    return () => {};
  }, [client?.businessUnitId, isMobile]);

  const handleEditClient = () => {
    setIsEditing(!isEditing);
  };

  const handleCancelEdit = () => {
    // Reset the draft to the original client values
    if (client) {
      setClientDraft({
        firstName: client.firstName,
        lastName: client.lastName,
        hasValidName: client.hasValidName,
        rut: client.rut || undefined,
        dni: client.dni || undefined,
        email: client.email,
        salary: client.salary,
        savingsAmount: client.savingsAmount || undefined,
        hasVehicleAsPartPayment: client.hasVehicleAsPartPayment,
        requiresFinancing: client.requiresFinancing,
        monthlyPaymentsAmount: client.monthlyPaymentsAmount || undefined,
        budgetAmount: client.budgetAmount || undefined,
        modelsOfInterest: client.modelsOfInterest || [],
        disabledAutomaticResponses: client.disabledAutomaticResponses || false,
        disabledFollowUps: client.disabledFollowUps || false,
        isTestClient: client.isTestClient || false,
        brandOfInterest: client.brandOfInterest || undefined,
        aiSafetyStatus: client.aiSafetyStatus || undefined,
        notes: client.notes || undefined,
        gender: client.gender || undefined,
        dateOfBirth: client.dateOfBirth || undefined,
        nationality: client.nationality || undefined,
        address: client.address && typeof client.address === 'object' ? client.address : undefined,
      });
    }
    setIsEditing(false);
  };

  const handleSaveClient = () => {
    if (!clientDraft || !client) return;
    setSaveError(null);
    let body = clientDraft;
    const rawRut = clientDraft.rut?.trim();
    if (rawRut) {
      const formatted = formatRut(rawRut);
      if (!formatted) {
        setSaveError('El RUT ingresado no tiene un formato válido.');
        return;
      }
      body = { ...clientDraft, rut: formatted };
      setClientDraft(body);
    }
    const onSuccess = () => {
      notifySaved('Cliente actualizado');
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    };
    const onError = (err: unknown) => {
      const message = (err as { data?: { error?: string } })?.data?.error;
      setSaveError(message ?? 'No se pudo actualizar el cliente.');
    };
    if (currentUser?.isAdmin) {
      updateClient({ id: client.id, body }).unwrap().then(onSuccess).catch(onError);
      return;
    }
    updateClientEditable({
      id: client.id,
      body: {
        firstName: body.firstName ?? null,
        lastName: body.lastName ?? null,
        email: body.email ?? null,
        rut: body.rut ?? null,
        dni: body.dni ?? null,
        gender: body.gender ?? null,
        nationality: body.nationality ?? null,
        dateOfBirth: body.dateOfBirth ?? null,
        address: body.address ?? null,
      },
    })
      .unwrap()
      .then(onSuccess)
      .catch(onError);
  };

  const handleRefetchClient = () => {
    refetchClient().catch((err) => console.error(err));
    refetchMessages().catch((err) => console.error(err));
    refetchClientLeads().catch((err) => console.error(err));
  };

  if (messaging) {
    onMessage(messaging, (payload) => {
      console.log('Message received in client:', payload);
      if (payload.data?.identifier === 'new-client-message' || payload.data?.identifier === 'client-auto-released') {
        refetchMessages().catch((e) => {
          console.error(e);
        });
        const notificationAudio = new Audio('/new-message-sound.wav');
        notificationAudio.play().catch((e) => {
          console.error(e);
        });
      }
    });
  }

  if (!clientId) {
    return <ErrorPage />;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Suspense fallback={<CircularProgress />}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: SURFACE,
            p: 3,
          }}
        >
          <Box
            sx={{
              borderRadius: RADIUS_CARD,
              px: { xs: 3, md: 6 },
              py: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              maxWidth: 680,
              width: '100%',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontFamily: 'Jost, sans-serif',
                color: NAVY,
                mb: 2,
              }}
            >
              No se encontró el cliente
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Jost, sans-serif',
                color: alpha(NAVY, 0.65),
                fontWeight: 500,
                mb: 1,
              }}
            >
              Algunas razones posibles son:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, textAlign: 'left' }}>
              {[
                'El cliente no existe',
                'No tienes permisos para acceder a este cliente',
                'El cliente está asignado a otro ejecutivo y no tienes acceso',
              ].map((reason) => (
                <Typography
                  key={reason}
                  component="li"
                  variant="body2"
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    color: alpha(NAVY, 0.55),
                    mb: 0.5,
                  }}
                >
                  {reason}
                </Typography>
              ))}
            </Box>
            <ErrorPage />
          </Box>
        </Box>
      </Suspense>
    );
  }

  const header = (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        mx: 1,
        mt: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 56,
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        boxShadow: SHADOW_CARD,
        borderRadius: RADIUS_CARD,
        zIndex: 1000,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Volver" arrow>
          <IconButton
            onClick={() => navigate(locationState?.from || `/clients${location.search}`)}
            size="medium"
            sx={{
              color: alpha(NAVY, 0.7),
              '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {showAttributes && isMobile && (
          <Button
            onClick={() => setShowAttributes(false)}
            sx={{
              color: NAVY,
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: RADIUS_COIN,
              '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
            }}
          >
            Conversación
          </Button>
        )}
        {!showAttributes && isMobile && (
          <Button
            onClick={() => setShowAttributes(true)}
            sx={{
              color: NAVY,
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: RADIUS_COIN,
              '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
            }}
          >
            {client.firstName || client.lastName || client.phone}
          </Button>
        )}
      </Box>
      <Box>
        {(!showAttributes || !isMobile) && (
          <ChatHeader
            client={client}
            isLoading={isFetching || isLoadingMessages}
            refetch={handleRefetchClient}
            setIsFilteredConversation={setIsFilteredConversation}
            isFilteredConversation={isFilteredConversation}
            isLastLeadClosed={[...(clientLeads?.leads ?? [])].sort((a, b) => b.id - a.id)[0]?.status === 'CLOSED'}
            windowExpiresAt={windowExpiresAt}
          />
        )}
      </Box>
    </Box>
  );

  const showSidebar = !isMobile && clientsList.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100vh',
      }}
    >
      {showSidebar && (
        <ClientSidebar
          clients={clientsList}
          currentClientId={clientId}
          newEvents={newEvents}
          onClose={() => {
            setClientsList([]);
            setNewEvents([]);
          }}
          onClientSelect={(selectedClientId) => {
            setNewEvents(newEvents.filter((event) => event.clientId !== selectedClientId));
            navigate(`/clients/${selectedClientId}${location.search}`, {
              state: {
                clientsList,
                newEvents: newEvents.filter((event) => event.clientId !== selectedClientId),
                from: locationState?.from,
              },
            });
          }}
        />
      )}
      <Box
        sx={{
          flex: '1 1 0%',
          width: showSidebar ? 'calc(100% - 250px)' : '100%',
          display: 'flex',
          flexDirection: 'row',
          minHeight: 0,
          overflow: 'hidden',
          m: 0,
          p: 0,
        }}
      >
        {(!showAttributes || !isMobile) && (
          <Box
            sx={{
              flexDirection: 'column',
              display: 'flex',
              flex: 5,
              minHeight: 0,
              overflow: 'hidden',
              backgroundColor: SURFACE_CHAT,
            }}
          >
            {header}
            <Box
              sx={{
                backgroundColor: SURFACE_CHAT,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {isLoadingMessages && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {!isLoadingMessages && conversation.length > 0 && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Conversation
                    conversation={conversation}
                    isFilteredConversation={isFilteredConversation}
                    fullHeight
                    scheduledMessages={scheduledMessages}
                    leads={clientLeads?.leads}
                    businessUnitId={client?.businessUnitId}
                  />
                </Box>
              )}
              {!isLoadingMessages && conversation.length === 0 && <Box sx={{ flex: 1, minHeight: 0 }} />}
              <Box
                sx={{
                  flexShrink: 0,
                }}
              >
                {isConversationActive ? (
                  <ChatFooter
                    client={client}
                    conversation={conversation}
                    setConversation={setConversation}
                    addScheduledMessage={(message) =>
                      setScheduledMessages((curr) => [...curr, message] as ScheduledMessage[])
                    }
                    isConversationActive={isConversationActive}
                    onOpenTemplateModal={() => setOpenTemplateModal(true)}
                  />
                ) : (
                  <QuickTemplates
                    client={client}
                    isEmptyConversation={conversation.length === 0}
                    onOpenFullModal={() => setOpenTemplateModal(true)}
                    onSent={() => refetchMessages()}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
        {(showAttributes || !isMobile) && (
          <Box
            sx={{
              flex: 3,
              minHeight: 0,
              overflow: 'hidden',
              backgroundColor: SURFACE_CHAT,
              maxWidth: isMobile ? '100%' : '37%',
            }}
          >
            {isMobile && header}
            <Box
              sx={{
                overflow: 'auto',
                height: '100%',
                backgroundColor: SURFACE_CHAT,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {!isEditing ? (
                <ClientInfoPanel
                  client={client}
                  businessUnit={clientDealership}
                  currentUser={currentUser}
                  clientLeads={clientLeads?.leads}
                  isCompact={isMobile}
                  isConversationActive={isConversationActive}
                  onEditClient={handleEditClient}
                />
              ) : (
                clientDraft && (
                  <EditClientForm
                    client={client}
                    clientDraft={clientDraft}
                    setClientDraft={setClientDraft}
                    onSave={handleSaveClient}
                    onCancel={handleCancelEdit}
                    currentUser={currentUser}
                    clientDealership={clientDealership}
                    isSaving={isSavingClient || isSavingClientEditable}
                    saveError={
                      updateClientError || updateClientEditableError ? 'Inténtalo nuevamente en unos segundos.' : null
                    }
                  />
                )
              )}
            </Box>
          </Box>
        )}
        {/* Success Snackbar */}
        <Snackbar
          open={saveSuccess}
          autoHideDuration={3000}
          onClose={() => setSaveSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSaveSuccess(false)} severity="success">
            Cliente guardado exitosamente
          </Alert>
        </Snackbar>
        {/* Error Snackbar */}
        <Snackbar
          open={!!saveError}
          autoHideDuration={5000}
          onClose={() => setSaveError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSaveError(null)}
            severity="error"
            sx={{
              width: '100%',
              backdropFilter: GLASS_BLUR,
              backgroundColor: 'rgba(253, 237, 237, 0.92)',
              borderRadius: RADIUS_CARD,
              boxShadow: SHADOW,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
            }}
          >
            {saveError}
          </Alert>
        </Snackbar>
        {/* Template Modal */}
        {client && openTemplateModal && (
          <TemplateMessageModal
            client={client}
            businessUnitId={client.businessUnitId}
            open={openTemplateModal}
            setOpen={setOpenTemplateModal}
            refetch={handleRefetchClient}
            allowTemplateCreation={currentUser?.allowTemplateCreation || false}
          />
        )}
      </Box>
    </Box>
  );
};

export default Chat;
